import './MapLegend.css';
import type { ComponentChildren } from 'preact';
import { type CopyKey, t } from '../../copy/strings';
import { joinList } from '../../format/agree';
import { fmtInt, fmtPct, fmtQtyShort } from '../../format/numbers';
import { placeName } from '../../format/places';
import type { Candidate, PlaceCode, ResultsView } from '../../model/types';
import { useConfig, useFocusUf, useStatics, useView } from '../../state/hooks';
import { FALLBACK_PARTY, PARTIES, type Party, partyClass, partyOf } from '../../theme/parties';
import { earlyVeil } from './earlyVeil';
import { type LegendUnit, type MapLegendData, mapLegendData } from './mapLink';

const UNIT_KEY: Record<LegendUnit, CopyKey> = {
  municipalities: 'map.legend.unit.municipalities',
  states: 'map.legend.unit.states',
  cities: 'map.legend.unit.cities',
  zones: 'map.legend.unit.zones',
};

/** Substantivo da unidade concordando com N ("1 estado", "2 estados", §9.6). */
export const unitWord = (unit: LegendUnit, n: number): string => t(UNIT_KEY[unit], { N: n });

/** Fração → "18%" (inteiro, §9.6 "arredondados a inteiro"). */
const pctInt = (f: number): string => `${fmtInt(Math.round(f * 100))}%`;

/** Um quadrado + texto de contagem; o último leva o substantivo concordando com o próprio número. */
export interface CountItem {
  party: Party;
  text: string;
}

export type LegendModel =
  | { kind: 'margin'; counts: CountItem[] | null }
  | { kind: 'counted'; text: string }
  | {
      kind: 'candidate';
      party: Party;
      title: string;
      lo: string;
      loNarrow: string;
      hi: string;
      hint: string;
    }
  | { kind: 'governor'; variants: Array<{ n: number; items: CountItem[] }> }
  | { kind: 'advantage'; counts: CountItem[] | null };

/** Itens de contagem com o substantivo no último (§9.6). */
function countItems(entries: Array<{ party: Party; n: number }>, unit: LegendUnit, others: number | null): CountItem[] {
  const items = entries.map((e) => ({ party: e.party, text: t('legend.count', { SIGLA: e.party.display, N: e.n }) }));
  const lastN = others !== null ? others : (entries[entries.length - 1]?.n ?? 0);
  if (others !== null) items.push({ party: FALLBACK_PARTY, text: t('legend.others', { N: others }) });
  const last = items[items.length - 1];
  if (last) last.text = `${last.text} ${unitWord(unit, lastN)}`;
  return items;
}

/** "{N} de {total} {unidade} apurados" (§9.6; feminino em cidades e zonas). */
export function countedText(unit: LegendUnit, n: number, total: number): string {
  switch (unit) {
    case 'municipalities':
      return t('legend.counted', { N: n, M: total });
    case 'cities':
      return t('legend.counted.cities', { N: n, M: total });
    case 'zones':
      return t('legend.counted.zones', { N: n, M: total });
    case 'states':
      return t('legend.counted.states', { N: n, M: total });
  }
}

/** Título do modo Candidato (§9.6, tabela "Título do modo Candidato"). */
export function candidateTitle(d: MapLegendData, view: ResultsView | null, side: 0 | 1): string {
  const S = view?.national.shares[side] ?? 0;
  if (!view || !(view.national.validVotes > 0)) return t('legend.waiting');
  const c = d.candidate;
  if (c && c.ahead >= 1) {
    switch (d.unit) {
      case 'municipalities':
        return t('legend.candidate.ahead', { N: c.ahead });
      case 'cities':
        return t('legend.candidate.ahead.cities', { N: c.ahead });
      case 'zones':
        return t('legend.candidate.ahead.zones', { N: c.ahead });
      case 'states':
        return t('legend.candidate.ahead.states', { N: c.ahead });
    }
  }
  if (c?.strongest) {
    return t('legend.candidate.strongest', {
      LUGAR: placeName(c.strongest.code as PlaceCode),
      P: fmtPct(c.strongest.share),
    });
  }
  return t('legend.candidate.country', { P: fmtPct(S) });
}

/** Partido de um `party_code` (com o fallback de §3.3.9, sem aviso repetido). */
const partyByCode = (code: string): Party => PARTIES[code] ?? partyOf(code);

/**
 * Conteúdo da legenda por contexto (§9.6, tabela "Conteúdo por contexto"), sempre {A} antes de {B} no Presidente.
 * null = legenda oculta (geometria carregando, Governadores com UF em foco, Exterior até a Fase 11).
 */
export function legendModel(
  d: MapLegendData | null,
  pair: readonly [Candidate, Candidate] | null,
  view: ResultsView | null,
  focus: boolean,
): LegendModel | null {
  if (!d) return null;
  if (d.office === 'governor') {
    if (focus) return null;
    const entries = d.parties.map((p) => ({ party: partyByCode(p.party), n: p.n }));
    const variants = [4, 2, 1].map((n) => {
      const shown = entries.slice(0, n);
      const rest = entries.slice(n).reduce((s, e) => s + e.n, 0);
      return { n, items: countItems(shown, 'states', rest > 0 ? rest : null) };
    });
    return { kind: 'governor', variants };
  }
  if (d.office !== 'president' || !pair || d.level === 'world') return null;
  const parties: [Party, Party] = [partyOf(pair[0]), partyOf(pair[1])];
  switch (d.mode) {
    case 'counted':
      return { kind: 'counted', text: countedText(d.unit, d.complete, d.total) };
    case 'candidate': {
      const side = d.candidate?.side ?? 0;
      const steps = d.candidate?.steps ?? [0, 0, 0, 0, 0];
      const S = view?.national.shares[side] ?? 0;
      const lo = steps[0] ?? 0;
      const hi = steps[4] ?? 0;
      return {
        kind: 'candidate',
        party: parties[side],
        title: candidateTitle(d, view, side),
        lo: t('legend.candidate.ramp.lo', { P1: pctInt(lo) }),
        loNarrow: `< ${pctInt(lo)}`,
        hi: t('legend.candidate.ramp.hi', { P2: pctInt(hi) }),
        hint: t('legend.candidate.title', {
          NOME: pair[side].display_name,
          P: fmtPct(S),
          LISTA: `${joinList(steps.map((s) => fmtInt(Math.round(s * 100))))}%`,
        }),
      };
    }
    case 'advantage': {
      if (!view || d.withData === 0) return { kind: 'advantage', counts: null };
      const sums: [number, number] = [0, 0];
      for (const p of Object.values(view.places)) {
        if (p.code !== 'ZZ' && p.leader !== null && p.sectionsFraction > 0) sums[p.leader] += Math.abs(p.diffVotes);
      }
      const counts = [0, 1].map((i) => ({
        party: parties[i] as Party,
        text: t('legend.advantage.party', { SIGLA: (parties[i] as Party).display, Q: fmtQtyShort(sums[i] as number) }),
      }));
      return { kind: 'advantage', counts };
    }
    default: {
      if (d.withData === 0) return { kind: 'margin', counts: null };
      const counts = countItems(
        [
          { party: parties[0], n: d.lead[0] },
          { party: parties[1], n: d.lead[1] },
        ],
        d.unit,
        null,
      );
      return { kind: 'margin', counts };
    }
  }
}

const Swatch = ({ party, apur }: { party?: Party; apur?: boolean }) => (
  <i
    class={`map-legend__swatch${apur ? ' map-legend__swatch--apur' : ''}${party ? ` ${partyClass(party)}` : ''}`}
    aria-hidden="true"
  />
);

function Counts({ items, class: extra = '' }: { items: CountItem[]; class?: string }) {
  return (
    <>
      {items.map((it, i) => (
        <span key={`${i}:${it.party.code}`} class={`map-legend__count ${extra}`}>
          <Swatch party={it.party} />
          {it.text}
        </span>
      ))}
    </>
  );
}

/** Rampa com pontas e texto final (§9.6): as pontas e o texto ficam fora do `role="img"` para serem lidos. */
function Scale(props: {
  label: string;
  title?: string;
  cells: ComponentChildren;
  lo?: ComponentChildren;
  hi?: string;
  text?: string;
  kind: string;
}) {
  return (
    <span class={`map-legend__scale map-legend__scale--${props.kind}`}>
      {props.lo ? <em class="map-legend__end">{props.lo}</em> : null}
      <span class="map-legend__ramp" role="img" aria-label={props.label} title={props.title ?? props.label}>
        {props.cells}
      </span>
      {props.hi ? <em class="map-legend__end">{props.hi}</em> : null}
      {props.text ? <em class="map-legend__text">{props.text}</em> : null}
    </span>
  );
}

const STEPS4 = [0, 1, 2, 3] as const;
const STEPS5 = [0, 1, 2, 3, 4] as const;
const STEPS6 = [0, 1, 2, 3, 4, 5] as const;

function MarginRamp({ pair }: { pair: readonly [Party, Party] }) {
  return (
    <Scale
      kind="margin"
      label={t('map.legend.margin.aria')}
      title={t('map.legend.margin.title')}
      text={t('legend.ramp.margin')}
      cells={STEPS4.map((i) => (
        <span key={i} class="map-legend__cell map-legend__cell--double">
          <i class={`map-legend__half map-legend__half--m${i} ${partyClass(pair[0])}`} />
          <i class={`map-legend__half map-legend__half--m${i} ${partyClass(pair[1])}`} />
        </span>
      ))}
    />
  );
}

/**
 * Legenda do mapa (§9.6), na barra do mapa: contagens (mesmo `MapPaint` do desenho, via `mapLegendData`), rampas e
 * título do Candidato. Texto real no DOM, ligado ao canvas por `aria-describedby="map-legend"`. Oculta até o 1º
 * desenho; as larguras estreitas são container queries (`map-legend` e `map-top`).
 */
export function MapLegend() {
  const data = mapLegendData.value;
  const view = useView();
  const statics = useStatics();
  const focus = useFocusUf() !== null;
  const pair = view?.candidates ?? statics.candidates?.president ?? null;
  const model = legendModel(data, pair, view, focus);
  const veil = earlyVeil(view, useConfig().features.early_veil);
  const parties: [Party, Party] | null = pair ? [partyOf(pair[0]), partyOf(pair[1])] : null;

  let body: ComponentChildren = null;
  if (model) {
    switch (model.kind) {
      case 'margin':
        body = (
          <>
            {model.counts ? <Counts items={model.counts} /> : null}
            {parties ? <MarginRamp pair={parties} /> : null}
          </>
        );
        break;
      case 'counted':
        body = (
          <>
            <span class="map-legend__count map-legend__count--counted">
              <Swatch apur />
              {model.text}
            </span>
            <Scale
              kind="counted"
              label={t('map.legend.counted.title')}
              lo={
                <>
                  <span class="map-legend__wide">{t('legend.ramp.counted.lo')}</span>
                  <span class="map-legend__narrow">{t('legend.ramp.counted.lo.narrow')}</span>
                </>
              }
              hi={t('legend.ramp.counted.hi')}
              cells={STEPS5.map((i) => <span key={i} class={`map-legend__cell map-legend__cell--apur${i}`} />)}
            />
          </>
        );
        break;
      case 'candidate':
        body = (
          <>
            <span class="map-legend__title">{model.title}</span>
            <Scale
              kind="candidate"
              label={model.hint}
              lo={
                <>
                  <span class="map-legend__wide">{model.lo}</span>
                  <span class="map-legend__narrow">{model.loNarrow}</span>
                </>
              }
              hi={model.hi}
              cells={STEPS6.map((i) => (
                <span key={i} class={`map-legend__cell map-legend__cell--c${i} ${partyClass(model.party)}`} />
              ))}
            />
          </>
        );
        break;
      case 'governor':
        body = (
          <>
            {model.variants.map((v) => (
              <span key={v.n} class={`map-legend__group map-legend__group--n${v.n}`}>
                <Counts items={v.items} />
              </span>
            ))}
            <Scale
              kind="governor"
              label={t('map.legend.governor.title')}
              text={t('legend.governors.ramp')}
              cells={[
                <span key="0" class="map-legend__cell map-legend__cell--neutral0" />,
                <span key="3" class="map-legend__cell map-legend__cell--neutral3" />,
              ]}
            />
          </>
        );
        break;
      case 'advantage':
        body = (
          <>
            {model.counts ? <Counts items={model.counts} /> : null}
            <em class="map-legend__text map-legend__text--advantage" title={t('map.legend.advantage.title')}>
              {t('map.legend.advantage.text')}
            </em>
          </>
        );
        break;
    }
  }

  return (
    <div
      class="map-legend"
      id="map-legend"
      data-testid="map-legend"
      data-kind={model?.kind}
      data-empty={data && data.withData === 0 ? '' : undefined}
      hidden={model === null}
    >
      {/* item 1.10: o véu de pouco apurado vem primeiro (a legenda tem 2 linhas; as contagens dizem pouco abaixo de 1%) */}
      {veil ? (
        <span class="map-legend__early" data-testid="map-legend-early">
          {t('map.legend.early')}
        </span>
      ) : null}
      {body}
    </div>
  );
}
